/** @jsx jsx */
import { jsx } from '@emotion/core';
import { withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import defaultsDeep from 'lodash/defaultsDeep';
import compose from 'lodash/fp/compose';
import { withNamespaces } from 'react-i18next';
import Spacer from '../../ui/Spacer';
import { Container, Row, Column } from '../../../styles/Grid';
import SolutionsCardsHeader from './SolutionsCardsHeader';
import MiscCard from './MiscCard';
import SolutionCard from '../../ui/SolutionCard';
import { SafePureComponent } from '../../../utils/SafePureComponent';
import {
  transformWildcardsToText,
  getWildcardData,
} from '../../../utils/wildcards';
import {
  spacerMarginCss,
  spacerPaddingCss,
  solutionCardsGridCss,
  cardColumnCss,
} from './index.styles';

const defaultData = {
  BackgroundColor: { fields: { Value: { value: 'transparent' } } },
  IsLocation: {
    value: false,
  },
  SolutionsCardsHeadline: {
    value: '',
  },
  SolutionsCardsBodyDescription: {
    value: '',
  },
  SolutionsCardsCTA: {
    value: {
      href: '#',
      linktype: 'internal',
      text: '',
      anchor: '',
      querystring: '',
      target: '',
      id: '',
    },
  },
  Cards: [],
};

class SolutionsCards extends SafePureComponent {
  constructor(props) {
    super(props);
    this.wildcards = getWildcardData(this.props.sitecoreContext);
  }

  /**
   * This function inject the wildcards values and transform that
   * values to be used in the Headline, Description and Cta
   * @param {Object} fields containt the information of the current component
   * @return {React.Node} Return the headline, description and link of the component
   */
  renderSolutionCardsHeader = fields => {
    const { t } = this.props;
    const contextFields = get(this.props, 'sitecoreContext.route.fields', {});
    const componentName = get(this.props, 'rendering.componentName');
    const baseWildcardValuesToTransform = {
      componentName,
      contextFields,
      fields,
      t,
      wildcards: this.wildcards,
    };
    const Headline = {
      value: transformWildcardsToText({
        ...baseWildcardValuesToTransform,
        text: get(fields, 'SolutionsCardsHeadline.value', ''),
      }),
    };

    const Description = {
      value: transformWildcardsToText({
        ...baseWildcardValuesToTransform,
        text: get(fields, 'SolutionsCardsBodyDescription.value', ''),
      }),
    };

    fields.SolutionsCardsCTA.value.text = transformWildcardsToText({
      ...baseWildcardValuesToTransform,
      text: get(fields, 'SolutionsCardsCTA.value.text', ''),
    });

    return (
      <SolutionsCardsHeader
        headline={Headline}
        description={Description}
        cta={fields.SolutionsCardsCTA}
      />
    );
  };

  safeRender() {
    const { props } = this;
    const isEditing = get(props, 'sitecoreContext.pageEditing', false);
    const fields = defaultsDeep(props.fields, defaultData);
    const haveCards =
      (fields.Cards && fields.Cards.length > 0) || fields.MiscCard;

    if (!haveCards && !isEditing) {
      return null;
    }

    return (
      <Spacer
        id="SolutionsCards"
        marginCss={spacerMarginCss}
        paddingCss={spacerPaddingCss}
        backgroundColor={fields.BackgroundColor}
        containerProps={{ 'data-sticky-id': get(props, 'rendering.uid') }}
      >
        <Container>
          {this.renderSolutionCardsHeader(fields)}
          <Row css={solutionCardsGridCss}>
            {fields.Cards.map((card, i) => (
              <Column
                css={cardColumnCss}
                key={`solution-card-${i}`}
                cols={{ sm: 6, md: 4, lg: 4, xl: 4 }}
              >
                <SolutionCard
                  fields={card}
                  isLocation={get(fields, 'IsLocation.value')}
                />
              </Column>
            ))}
            {fields.MiscCard && (
              <Column cols={{ sm: 6, md: 4, lg: 4, xl: 4 }} css={cardColumnCss}>
                <MiscCard fields={fields.MiscCard} />
              </Column>
            )}
          </Row>
        </Container>
      </Spacer>
    );
  }
}

SolutionsCards.defaultProps = { fields: defaultData };

SolutionsCards.propTypes = {
  fields: PropTypes.shape({
    IsLocation: PropTypes.shape({
      value: PropTypes.bool,
    }),
    SolutionsCardsHeadline: PropTypes.shape({
      value: PropTypes.string,
    }),
    SolutionsCardsBodyDescription: PropTypes.shape({
      value: PropTypes.string,
    }),
    SolutionsCardsCTA: PropTypes.shape({
      value: PropTypes.shape({
        href: PropTypes.string,
        linktype: PropTypes.string,
        text: PropTypes.string,
        anchor: PropTypes.string,
        querystring: PropTypes.string,
        target: PropTypes.string,
        id: PropTypes.string,
      }),
    }),
    SolutionsCards: PropTypes.arrayOf(
      PropTypes.shape({
        fields: PropTypes.shape({
          CardHeadline: PropTypes.shape({
            value: PropTypes.string,
          }),
          CardPriceLine: PropTypes.shape({
            value: PropTypes.string,
          }),
          CardShortDescription: PropTypes.shape({
            value: PropTypes.string,
          }),
          CardChecklistHeadline: PropTypes.shape({
            value: PropTypes.string,
          }),
          CardChecklistItemList: PropTypes.arrayOf(PropTypes.string),
        }),
      }),
    ),
  }),
};

SolutionsCards.displayName = 'SolutionsCards';

export default compose(withSitecoreContext(), withNamespaces())(SolutionsCards);
